import { Directive, ElementRef, HostListener, OnDestroy, Renderer2 } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';

@Directive({
  selector: 'sg-text[sgCopyOnHover]',
  standalone: false,
})
export class SgCopyOnHoverDirective implements OnDestroy {
  private static readonly BUTTON_SIZE = 22;
  private static readonly OFFSET = 4;
  private static readonly HIDE_DELAY = 500;

  private static readonly BASE_STYLES = {
    position: 'fixed',
    cursor: 'pointer',
    border: 'none',
    width: `${SgCopyOnHoverDirective.BUTTON_SIZE}px`,
    height: `${SgCopyOnHoverDirective.BUTTON_SIZE}px`,
    borderRadius: '50%',
    fontSize: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0',
    opacity: '0',
    pointerEvents: 'none',
    boxShadow: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)',
    transform: 'scale(0.95)',
    zIndex: '1000',
    fontFamily: 'Material Icons',
    transition:
      'opacity 0.2s ease-in-out, transform 0.2s ease-in-out, background 0.2s ease-in-out, color 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
  };

  private static readonly DEFAULT_STYLES = {
    background: 'var(--sg-bg-accent-magenta-tertiary, #f8f0ff)',
    color: 'var(--sg-content-accent-magenta-primary, #c832e0)',
    transform: 'scale(0.95)',
    boxShadow: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)',
  };

  private static readonly BUTTON_HOVER_STYLES = {
    background: 'var(--sg-bg-accent-magenta-secondary, #f0e1ff)',
    color: 'var(--sg-content-accent-magenta-primary, #c832e0)',
    transform: 'scale(1.05)',
    boxShadow: '0 3px 6px rgba(0,0,0,0.16), 0 3px 6px rgba(0,0,0,0.23)',
  };

  private static readonly BUTTON_CLICK_STYLES = {
    transform: 'scale(0.9)',
  };

  // Static properties for shared button management
  private static copyButton: HTMLElement | null = null;
  private static activeDirective: SgCopyOnHoverDirective | null = null;
  private static hideTimeout: number | null = null;
  private static renderer: Renderer2;

  constructor(
    private el: ElementRef<HTMLElement>,
    renderer: Renderer2,
    private snackBar: MatSnackBar
  ) {
    // Store renderer statically for shared use
    SgCopyOnHoverDirective.renderer = renderer;
  }

  @HostListener('mouseenter')
  onMouseEnter(): void {
    SgCopyOnHoverDirective.clearHideTimeout();

    // Set this as the active directive
    SgCopyOnHoverDirective.activeDirective = this;

    if (!SgCopyOnHoverDirective.copyButton) {
      this.createCopyButton();
    }
    this.positionAndShowCopyButton();
  }

  @HostListener('mouseleave')
  onMouseLeave(): void {
    if (SgCopyOnHoverDirective.activeDirective === this) {
      SgCopyOnHoverDirective.scheduleHide(this);
    }
  }

  private static clearHideTimeout(): void {
    if (SgCopyOnHoverDirective.hideTimeout) {
      clearTimeout(SgCopyOnHoverDirective.hideTimeout);
      SgCopyOnHoverDirective.hideTimeout = null;
    }
  }

  private static scheduleHide(instance: SgCopyOnHoverDirective): void {
    SgCopyOnHoverDirective.clearHideTimeout();
    SgCopyOnHoverDirective.hideTimeout = window.setTimeout(
      () => instance.hideCopyButton(),
      SgCopyOnHoverDirective.HIDE_DELAY
    );
  }

  private createCopyButton(): void {
    // Ensure only one button exists globally
    if (SgCopyOnHoverDirective.copyButton) return;

    SgCopyOnHoverDirective.copyButton = SgCopyOnHoverDirective.renderer.createElement('button');

    // Set attributes and classes
    SgCopyOnHoverDirective.renderer.setAttribute(SgCopyOnHoverDirective.copyButton, 'type', 'button');
    SgCopyOnHoverDirective.renderer.setAttribute(SgCopyOnHoverDirective.copyButton, 'aria-label', 'Copy text');
    SgCopyOnHoverDirective.renderer.addClass(SgCopyOnHoverDirective.copyButton, 'sg-copy-button');
    SgCopyOnHoverDirective.renderer.addClass(SgCopyOnHoverDirective.copyButton, 'material-icons');
    SgCopyOnHoverDirective.renderer.setProperty(SgCopyOnHoverDirective.copyButton, 'textContent', 'content_copy');

    // Apply all styles at once
    this.applyButtonStyles();
    this.addButtonEventListeners();

    SgCopyOnHoverDirective.renderer.appendChild(document.body, SgCopyOnHoverDirective.copyButton);
  }

  private applyButtonStyles(): void {
    if (!SgCopyOnHoverDirective.copyButton) return;

    Object.entries({ ...SgCopyOnHoverDirective.BASE_STYLES, ...SgCopyOnHoverDirective.DEFAULT_STYLES }).forEach(
      ([property, value]) => {
        SgCopyOnHoverDirective.renderer.setStyle(SgCopyOnHoverDirective.copyButton, property, value);
      }
    );
  }

  private applyHoverStyles(): void {
    if (!SgCopyOnHoverDirective.copyButton) return;
    Object.entries(SgCopyOnHoverDirective.BUTTON_HOVER_STYLES).forEach(([property, value]) => {
      SgCopyOnHoverDirective.renderer.setStyle(SgCopyOnHoverDirective.copyButton, property, value);
    });
  }

  private applyDefaultStyles(): void {
    if (!SgCopyOnHoverDirective.copyButton) return;
    Object.entries(SgCopyOnHoverDirective.DEFAULT_STYLES).forEach(([property, value]) => {
      SgCopyOnHoverDirective.renderer.setStyle(SgCopyOnHoverDirective.copyButton, property, value);
    });
  }

  private applyClickStyles(): void {
    if (!SgCopyOnHoverDirective.copyButton) return;
    Object.entries(SgCopyOnHoverDirective.BUTTON_CLICK_STYLES).forEach(([property, value]) => {
      SgCopyOnHoverDirective.renderer.setStyle(SgCopyOnHoverDirective.copyButton, property, value);
    });
  }

  private addButtonEventListeners(): void {
    if (!SgCopyOnHoverDirective.copyButton) return;

    SgCopyOnHoverDirective.renderer.listen(SgCopyOnHoverDirective.copyButton, 'mouseenter', () => {
      SgCopyOnHoverDirective.clearHideTimeout();
      this.applyHoverStyles();
    });

    SgCopyOnHoverDirective.renderer.listen(SgCopyOnHoverDirective.copyButton, 'mouseleave', () => {
      this.applyDefaultStyles();
      if (SgCopyOnHoverDirective.activeDirective) {
        SgCopyOnHoverDirective.scheduleHide(SgCopyOnHoverDirective.activeDirective);
      }
    });

    SgCopyOnHoverDirective.renderer.listen(SgCopyOnHoverDirective.copyButton, 'click', (event: Event) => {
      event.stopPropagation();
      this.applyClickStyles();
      setTimeout(() => {
        if (SgCopyOnHoverDirective.copyButton) {
          this.applyHoverStyles();
        }
      }, 100);
      // Handle async operation without awaiting in the listener
      this.copyTextToClipboard();
    });
  }

  private calculateButtonPosition(): { top: number; left: number } {
    const rect = this.el.nativeElement.getBoundingClientRect();
    const viewportHeight = document.documentElement.clientHeight;
    const parentTableCell = this.findParentTableCell();
    const parentWithOverflow = this.findParentWithOverflow();

    let top = rect.top + (rect.height - SgCopyOnHoverDirective.BUTTON_SIZE) / 2;
    let left = rect.left - SgCopyOnHoverDirective.BUTTON_SIZE - SgCopyOnHoverDirective.OFFSET;

    // Handle parent with overflow
    if (parentWithOverflow) {
      const parentRect = parentWithOverflow.getBoundingClientRect();
      if (left < parentRect.left) {
        left = parentRect.left + SgCopyOnHoverDirective.OFFSET;
      }
    }

    // Handle table cell boundaries
    if (parentTableCell) {
      const cellRect = parentTableCell.getBoundingClientRect();
      if (left < cellRect.left) {
        left = cellRect.left + SgCopyOnHoverDirective.OFFSET;
      }
    }

    // Handle viewport boundaries
    if (left < SgCopyOnHoverDirective.OFFSET) {
      left = rect.right + SgCopyOnHoverDirective.OFFSET;
    }

    if (top < 0) {
      top = SgCopyOnHoverDirective.OFFSET;
    } else if (top + SgCopyOnHoverDirective.BUTTON_SIZE > viewportHeight) {
      top = viewportHeight - SgCopyOnHoverDirective.BUTTON_SIZE - SgCopyOnHoverDirective.OFFSET;
    }

    return { top, left };
  }

  private positionAndShowCopyButton(): void {
    if (!SgCopyOnHoverDirective.copyButton) return;

    // Hide the button before repositioning
    SgCopyOnHoverDirective.renderer.setStyle(SgCopyOnHoverDirective.copyButton, 'opacity', '0');
    SgCopyOnHoverDirective.renderer.setStyle(SgCopyOnHoverDirective.copyButton, 'pointer-events', 'none');

    // Calculate and set position
    const { top, left } = this.calculateButtonPosition();
    SgCopyOnHoverDirective.renderer.setStyle(SgCopyOnHoverDirective.copyButton, 'top', `${top}px`);
    SgCopyOnHoverDirective.renderer.setStyle(SgCopyOnHoverDirective.copyButton, 'left', `${left}px`);

    // Show the button after a very short delay to avoid animation
    setTimeout(() => {
      if (SgCopyOnHoverDirective.copyButton && SgCopyOnHoverDirective.activeDirective === this) {
        SgCopyOnHoverDirective.renderer.setStyle(SgCopyOnHoverDirective.copyButton, 'opacity', '1');
        SgCopyOnHoverDirective.renderer.setStyle(SgCopyOnHoverDirective.copyButton, 'pointer-events', 'auto');
      }
    }, 50);
  }

  private findParentTableCell(): HTMLElement | null {
    let parent = this.el.nativeElement.parentElement;
    while (parent) {
      if (parent.tagName === 'TD' || parent.tagName === 'TH') {
        return parent;
      }
      parent = parent.parentElement;
    }
    return null;
  }

  private findParentWithOverflow(): HTMLElement | null {
    let parent = this.el.nativeElement.parentElement;
    while (parent) {
      const style = window.getComputedStyle(parent);
      if (style.overflow === 'hidden' || style.overflowX === 'hidden') {
        return parent;
      }
      parent = parent.parentElement;
    }
    return null;
  }

  private hideCopyButton(): void {
    if (!SgCopyOnHoverDirective.copyButton) return;

    SgCopyOnHoverDirective.renderer.setStyle(SgCopyOnHoverDirective.copyButton, 'opacity', '0');
    SgCopyOnHoverDirective.renderer.setStyle(SgCopyOnHoverDirective.copyButton, 'pointer-events', 'none');
    SgCopyOnHoverDirective.activeDirective = null;
  }

  private async copyTextToClipboard(): Promise<void> {
    // Ensure we're copying from the active directive's element
    if (!SgCopyOnHoverDirective.activeDirective) return;

    const text = SgCopyOnHoverDirective.activeDirective.el.nativeElement.textContent?.trim() ?? '';
    if (!text) return;

    try {
      await navigator.clipboard.writeText(text);
      this.showSuccessMessage();
    } catch (error) {
      this.showErrorMessage();
    }
  }

  private showSuccessMessage(): void {
    this.snackBar.open('Copied to clipboard', '', {
      duration: 2000,
      verticalPosition: 'top',
      panelClass: ['sg-snackbar-success'],
    });
  }

  private showErrorMessage(): void {
    this.snackBar.open('Failed to copy', '', {
      duration: 2000,
      verticalPosition: 'top',
      panelClass: ['sg-snackbar-error'],
    });
  }

  ngOnDestroy(): void {
    SgCopyOnHoverDirective.clearHideTimeout();

    // Only hide the button if this is the last instance
    if (SgCopyOnHoverDirective.activeDirective === this) {
      SgCopyOnHoverDirective.activeDirective = null;
      if (SgCopyOnHoverDirective.copyButton) {
        SgCopyOnHoverDirective.renderer.setStyle(SgCopyOnHoverDirective.copyButton, 'opacity', '0');
        SgCopyOnHoverDirective.renderer.setStyle(SgCopyOnHoverDirective.copyButton, 'pointer-events', 'none');
      }
    }
  }
}
